import {Helmet} from '@dr.pogodin/react-helmet';

import {useState} from 'react';

import {find, not} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {usePostTireOrderMutation} from '@dms/api/core';
import {useLazyGetVehicleCustomerQuery, usePostVehicleCustomerMutation} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {testIds, tiresInventoryRoutes} from '@dms/routes';
import {CustomerVehicleSelection, EntityHeader, handleApiError, Page, Section} from '@dms/shared';

import {composePath, getApiDateString, Nullish, useNavigate} from 'shared/web';

const OPERATOR_TYPE = 'CORVEHICLECUSTOMERTYPE_OPERATOR';

export function CreateTireOrder() {
  const navigate = useNavigate();

  const [getVehicleCustomer] = useLazyGetVehicleCustomerQuery();
  const [postVehicleCustomer] = usePostVehicleCustomerMutation();
  const [postTireOrder, {isLoading: isTireOrderCreating}] = usePostTireOrderMutation();
  const [customerId, setCustomerId] = useState<string | Nullish>(null);
  const [vehicleId, setVehicleId] = useState<string | Nullish>(null);

  const handleCreateTireOrder = async () => {
    if (!customerId) {
      throw new Error('missing customer Id');
    }

    const customers = vehicleId
      ? await getVehicleCustomer({vehicleId}).unwrap().catch(handleApiError)
      : undefined;

    const hasCustomerConnection = find(
      (vehicleCustomer) => vehicleCustomer?.customerId === customerId,
      customers ?? []
    );
    const hasVehicleOperator = find(
      (vehicleCustomer) => vehicleCustomer?.customerType === OPERATOR_TYPE,
      customers ?? []
    );

    if (not(hasCustomerConnection) && not(hasVehicleOperator) && vehicleId) {
      postVehicleCustomer({
        body: {
          customerType: OPERATOR_TYPE,
          customerId,
          vehicleId,
          eventId: vehicleId,
          eventType: 'vehicle',
          dateFrom: getApiDateString(new Date()),
        },
      })
        .unwrap()
        .catch(handleApiError);
    }

    postTireOrder({
      body: {
        vehicleId: vehicleId ?? undefined,
        customerId:
          !hasCustomerConnection || !hasVehicleOperator
            ? customerId
            : String(hasVehicleOperator.customerId),
      },
    })
      .unwrap()
      .then((response) => {
        if (response?.orderId) {
          navigate(
            composePath(tiresInventoryRoutes.tireOrderDetail, {
              params: {id: response?.orderId},
              queryParams: {source: 'tire-orders'},
            })
          );
        }
      })
      .catch(handleApiError);
  };

  return (
    <>
      <Helmet title={i18n.t('page.tiresInventory.labels.createTireOrder')} />
      <Page
        header={
          <EntityHeader
            data-testid={testIds.tiresInventory.createTireOrder('header')}
            title={i18n.t('page.tiresInventory.labels.newTireOrder')}
            icon="image/photo"
            actions={[
              {
                type: 'button',
                variant: 'secondary',
                title: i18n.t('general.actions.discard'),
                onClick: () => navigate(tiresInventoryRoutes.overview),
              },
              {
                type: 'button',
                title: i18n.t('general.actions.create'),
                onClick: handleCreateTireOrder,
                isDisabled: isNilOrEmpty(customerId),
                isLoading: isTireOrderCreating,
              },
            ]}
          />
        }
        data-testid={testIds.tiresInventory.createTireOrder('page')}
      >
        <Section>
          <CustomerVehicleSelection
            customerId={customerId}
            vehicleId={vehicleId}
            onCustomerChange={setCustomerId}
            onVehicleChange={setVehicleId}
            allowNoVehicle
            data-testid={testIds.tiresInventory.createTireOrder('customerVehicleSelection')}
          />
        </Section>
      </Page>
    </>
  );
}
